// ============================================================================
// Card utilities — pure helpers for the Flow chat card system
// (kept DOM-free so they are unit-testable without jsdom)
// ============================================================================

import { beoflowColors } from '@/theme/muiTheme'
import { hrZoneColor } from '@/theme/BeoflowZoneColors.generated'
import { sportColors } from '@/theme/BeoflowSportColors.generated'
import { activityTypeToSport, sportColor as sportEnumColor } from '@/types/workouts'
import type { RenderEntity, RenderStatus } from '@/types/chatRenders'

/** Render types the CardRenderer knows how to draw. Anything else falls back
 *  to the plain ToolCallCard pill. */
export const KNOWN_RENDER_TYPES = [
  'receipt',
  'workout_card',
  'chart',
  'training_week',
  'memory',
  'routine',
  'doc',
] as const

export function isKnownRenderType(type: string): boolean {
  return (KNOWN_RENDER_TYPES as readonly string[]).includes(type)
}

/** Status ring color: teal ok / yellow removed / red failed (design tokens). */
export function statusColor(status: RenderStatus): string {
  switch (status) {
    case 'removed':
      return beoflowColors.yellowBase
    case 'failed':
      return beoflowColors.redBase
    default:
      return beoflowColors.tealBase
  }
}

/**
 * Deep-link route for a render entity:
 *   workout / schedule_entry -> calendar
 *   widget / view            -> dashboards
 *   doc                      -> /docs/:id (DocPage)
 *   routine                  -> /routines (the routines management page)
 */
export function entityRoute(entity: RenderEntity): string | null {
  switch (entity.kind) {
    case 'workout':
    case 'schedule_entry':
      return '/calendar'
    case 'widget':
      return '/views'
    case 'view':
      return entity.id != null ? `/views/${entity.id}` : '/views'
    case 'routine':
      return '/routines'
    case 'doc':
      return entity.id != null ? `/docs/${entity.id}` : null
    default:
      return null
  }
}

/** Sport accent color from the generated tokens; tolerates enum-ish inputs
 *  ('running', 'RUNNING', 'Running', Garmin-style labels). */
export function sportDotColor(sport: string | null | undefined): string {
  if (!sport) return sportColors.other
  const key = sport.toLowerCase() as keyof typeof sportColors
  if (key in sportColors) return sportColors[key]
  return sportEnumColor(activityTypeToSport(sport))
}

/** Zone dot color: accepts 2, '2', 'z2', 'Z2', 'zone 2'; null -> dim grey. */
export function zoneDotColor(zone: number | string | null | undefined): string {
  if (zone == null || zone === '') return beoflowColors.graphGreyDark
  if (typeof zone === 'number') return hrZoneColor(zone)
  const match = zone.match(/\d+/)
  if (!match) return beoflowColors.graphGreyDark
  return hrZoneColor(parseInt(match[0], 10))
}

/** 'YYYY-MM-DD' + n days -> 'YYYY-MM-DD' (noon-anchored to dodge DST). */
export function addDaysIso(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T12:00:00`)
  d.setDate(d.getDate() + days)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** Local today as 'YYYY-MM-DD'. */
export function todayIso(now: Date = new Date()): string {
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** Short human date for mono meta rows ('Jul 10'). Returns null on bad input. */
export function formatShortDate(value: string | null | undefined): string | null {
  if (!value) return null
  const d = new Date(value.length <= 10 ? `${value}T12:00:00` : value)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/** Weekday + day + month, e.g. "Thu 16 Jul" — the receipt/workout kind-row date
 *  format in the design frames (rendered uppercase by the mono meta style). */
export function formatWeekdayDate(value: string | null | undefined): string | null {
  if (!value) return null
  const d = new Date(value.length <= 10 ? `${value}T12:00:00` : value)
  if (Number.isNaN(d.getTime())) return null
  const wd = d.toLocaleDateString('en-US', { weekday: 'short' })
  const mo = d.toLocaleDateString('en-US', { month: 'short' })
  return `${wd} ${d.getDate()} ${mo}`
}

/** Minutes -> "45 min" under an hour, "2h 30m" / "1h 02m" at or above. */
export function formatDuration(minutes: number | null | undefined): string {
  if (minutes == null || Number.isNaN(minutes)) return ''
  const m = Math.round(minutes)
  if (m < 60) return `${m} min`
  const h = Math.floor(m / 60)
  const rem = m % 60
  return rem ? `${h}h ${String(rem).padStart(2, '0')}m` : `${h}h`
}

/** ISO-8601 week number of a 'YYYY-MM-DD' date (Monday weeks; week 1 holds
 *  the year's first Thursday). Returns null on bad input. */
export function isoWeekNumber(isoDate: string): number | null {
  const d = new Date(`${isoDate}T12:00:00Z`)
  if (Number.isNaN(d.getTime())) return null
  d.setUTCDate(d.getUTCDate() + 3 - ((d.getUTCDay() + 6) % 7))
  const jan1 = Date.UTC(d.getUTCFullYear(), 0, 1)
  return Math.floor((d.getTime() - jan1) / 86_400_000 / 7) + 1
}

/** Day range of a week, e.g. "21 to 27 Sep" or "28 Sep to 4 Oct". */
export function formatWeekRange(weekStart: string): string | null {
  const start = new Date(`${weekStart}T12:00:00`)
  if (Number.isNaN(start.getTime())) return null
  const end = new Date(`${addDaysIso(weekStart, 6)}T12:00:00`)
  const month = (d: Date) => d.toLocaleDateString('en-US', { month: 'short' })
  const from = start.getMonth() === end.getMonth() ? `${start.getDate()}` : `${start.getDate()} ${month(start)}`
  return `${from} to ${end.getDate()} ${month(end)}`
}
